import { InputAdornment, OutlinedInput } from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { VisibilityOutlined, VisibilityOffOutlined } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import FormattedMessage from "features/common/TranslatedMessage";
import { useFormik } from "formik";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  setLogOut,
  setTokenAsync
} from "slices/loginSlice";
import * as Yup from "yup";
import React from "react";
import EnvelopeSimple from 'images/EnvelopeSimple.svg';
import Lock from 'images/Lock.svg';
import Graphics from "./Graphics";
import { isMSME, isSebi } from "Routes/Routes";

import { authenticateDocMgmt } from "Clients/ACCB/filesACCB/DocMgmtApi";
import instance from "api/globalaxios";

const LoginSchema = Yup.object().shape({
  emailOrPhone: Yup.string().trim()
    .required("Email or Phone Number Required")
    .test("is-email-or-phone", "Enter a valid email or 10-digit phone number", (value) => {
      if (!value) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\d{10}$/;
      return emailRegex.test(value.trim()) || phoneRegex.test(value.trim());
    }),
  password: Yup.string().required("Password Required"),
});

const footerLinks = [
  {
    title: "Terms & Conditions",
    value: "/files/termsAndConditions.pdf",
    newTab: true,
  },
  {
    title: "Privacy Policy",
    value: "/files/wenyayPrivacyPolicy.pdf",
    newTab: true,
  },
];

const renderLinks = (links, classes) => {
  return links.map((link, index) => {
    return (
      <span key={index}>
        <Link
          className={classes.link}
          to={{ pathname: link.value }}
          target={link.newTab ? "_blank" : "_self"}
        >
          {link.title}
        </Link>
        {index < links.length - 1 && <span>{' | '}</span>}
      </span>
    );
  });
};

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(6),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "2rem",
    [theme.breakpoints.up("lg")]: {
      marginTop: theme.spacing(8),
    },
    [theme.breakpoints.down("xs")]: {
      padding: 0,
    },
  },
  form: {
    width: "80%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  title: {
    fontSize: '2.625rem',
    fontWeight: 500,
    lineHeight: 'normal',
    textAlign: 'center',
    color: 'black',
    marginBottom: '0.75rem',
  },
  sub_title: {
    color: 'black',
    textAlign: 'center',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  color_gray: {
    color: TEXT.secondary,
    fontWeight: 400,
  },
  label: {
    color: TEXT.strong,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.3125rem',
  },
  input_field: {
    margin: '0.5rem 0',
    borderRadius: 0,
    width: '100%',
    boxSizing: 'border-box',
  },
  forgot_button: {
    color: TEXT.link,
    fontSize: '1rem',
    fontWeight: 500,
    textAlign: 'right',
    margin: '0.6rem 0',
    cursor: 'pointer',
  },
  forgot_row: {
    display: 'block',
    width: '100%',
    textDecoration: 'none',
  },
  main_button: {
    // Shares the inputs' box exactly, so its left and right edges line up with
    // the field borders above it.
    width: '100%',
    boxSizing: 'border-box',
    color: 'white',
    backgroundColor: theme.palette.primary.main,
    textTransform: 'none',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 0,
    padding: '0.7rem',
    boxShadow: 'none',
    fontSize: '1.125rem',
    fontWeight: 500,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: 'none',
    },
  },
  space_between_input_fields: {
    height: '1rem',
  },
  space_between_buttons: {
    height: '2.6rem',
  },
  leftHandSide: {
    width: '60%',
    ['@media (max-width: 700px)']: {
      display: 'none',
    },
  },
  rightHandSide: {
    width: '40%',
    backgroundColor: 'white',
    ['@media (max-width: 700px)']: {
      width: '100%',
    },
  },
  // E6: these were plain black, so they did not read as interactive next to the
  // blue "Forgot password?" link. Same link colour for every clickable text.
  link: {
    color: TEXT.primary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  }
}));

const Signin = () => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const { state: routeState } = useLocation();
  const [currentScreen, setCurrentScreen] = useState(2);
  const apiErrors = useSelector((state) => state.login.errors);
  const hasErrors = useSelector((state) => state.login.hasErrors);
  const formik = useFormik({
    initialValues: {
      emailOrPhone: "",
      password: "",
    },
    validationSchema: LoginSchema,
    onSubmit: (values) => {
      dispatch(setLogOut());
      let { emailOrPhone, password } = values;
      dispatch(setTokenAsync(emailOrPhone, password)).then(async () => {
        try {
          // Resolves the account whether the user typed an email or a phone. For
          // phone-only accounts user.email is the bac_temp_email placeholder — that
          // IS the doc-mgmt identity, so it must not be masked here.
          const { data: user } = await instance.get("/api/user/");
          await authenticateDocMgmt((user?.email || emailOrPhone).trim());
        } catch (err) {
          console.error("Doc mgmt auth failed on login:", err);
        }
        if (routeState) {
          history.push(`${routeState.from.pathname}`);
        } else {
          history.push("/");
        }
      });
    },
  });

  const [passwordType, setPasswordType] = useState("password");
  const tooglePasswordType = () => {
    if (passwordType === "password") {
      setPasswordType("text");
    } else {
      setPasswordType("password");
    }
  };

  React.useEffect(() => {
    dispatch(setLogOut()); // to remove login error alert
  }, []);

  return (
    <Grid container>
      <Grid item className={classes.leftHandSide} >
        <Graphics />
      </Grid>
      <Grid item className={classes.rightHandSide}>
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <Box className={classes.paper}>
            {/* <Box style={{width:'9rem'}}>
            <img src="/nalsar_logo.png" alt="logo" style={{width:'100%'}}/>
            <div style={{height:'1rem'}} />
          </Box> */}
            <Typography component="h1" className={classes.title}>
              <FormattedMessage id="auth.login" />
            </Typography>
            {/* <Typography className={`${classes.sub_title} ${classes.color_gray}`}>
            Your log in details have been shared over email
          </Typography> */}
            <Box style={{ height: '1.5rem' }} />
            <form
              className={classes.form}
              noValidate
              onSubmit={formik.handleSubmit}
            >
              <Box style={{ height: '1rem' }} />
              <Typography id="email" className={classes.label}>Email address or Phone Number</Typography>
              <OutlinedInput
                size="small"
                margin="normal"
                className={classes.input_field}
                value={formik.emailOrPhone}
                required
                id="emailOrPhone"
                name="emailOrPhone"
                autoComplete="emailOrPhone"
                placeholder="Email Address or Phone Number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                startAdornment={<InputAdornment position="start"><img src={EnvelopeSimple} alt="Envelope" /></InputAdornment>}
              />
              {formik.errors.emailOrPhone && formik.touched.emailOrPhone && (
                <Alert severity="error">{formik.errors.emailOrPhone}</Alert>
              )}
              {hasErrors && apiErrors.email && (
                <Alert severity="error">{apiErrors.email[0]}</Alert>
              )}
              <Box className={classes.space_between_input_fields} />
              <Typography id="password" className={classes.label}><FormattedMessage id="auth.password" /></Typography>
              <OutlinedInput
                size="small"
                className={classes.input_field}
                margin="normal"
                required
                name="password"
                type={passwordType}
                id="password"
                value={formik.password}
                autoComplete="current-password"
                placeholder="At least 6 characters"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                startAdornment={<InputAdornment position="start"><img src={Lock} alt="Lock" /></InputAdornment>}
                endAdornment={
                  <InputAdornment
                    position="end"
                    onClick={tooglePasswordType}
                    style={{ cursor: "pointer", color: TEXT.secondary }}
                  >
                    {passwordType === "password" ? (
                      <VisibilityOffOutlined />
                    ) : (
                      <VisibilityOutlined />
                    )}
                  </InputAdornment>
                }
              />
              {formik.errors.password && formik.touched.password && (
                <Alert severity="error">{formik.errors.password}</Alert>
              )}
              {hasErrors && apiErrors.password && (
                <Alert severity="error">{apiErrors.password[0]}</Alert>
              )}
              <Link to="/forgot-password" className={classes.forgot_row}>
                <Typography className={classes.forgot_button}>{<FormattedMessage id="auth.forgot" />}</Typography>
              </Link>
              <Box className={classes.space_between_buttons} />
              <Button
                size="small"
                type="submit"
                fullWidth
                variant="contained"
                className={classes.main_button}
              >
                {<FormattedMessage id="auth.login" />}
              </Button>
              {hasErrors && apiErrors.non_field_errors && (
                <Alert severity="error">{apiErrors.non_field_errors[0]}</Alert>
              )}
              <div style={{ height: '2rem' }} />
            </form>
          </Box>

          <Typography style={{ textAlign: 'center' }}>
            {renderLinks(footerLinks, classes)}
          </Typography>

          <div style={{ height: '1rem' }} />
        </Container>
      </Grid>
    </Grid>
  );
};

export default Signin;
